import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import {
  STAGES, STANDING_LABEL, STANDING_LEVEL, stageName, nz, nd, cell, lead, implied,
  FUNNEL_LEAD, NULL_NOTE, GAP_NOTE, AGE_NOTE, MONEY_NOTE, UNAVAILABLE_NOTE, LOADING_NOTE,
} from '../lib/bowlFunnel.js'
import styles from './BowlFunnel.module.css'

/**
 * THE FUNNEL — eligible → owned → reached out → artwork → deal → closed, per
 * team, with the latency between stages and an honest label on every stage
 * saying whether it can be measured at all.
 *
 * 🔴 THE DESIGN DECISION, AND IT IS THE WHOLE POINT. Five of these seven
 * stages cannot be computed today. The obvious renderer shows them as 0, and a
 * 0 in a funnel reads as "the team did nothing". The truth is "we cannot see
 * it", and a rep acts on those two very differently — one is a prompt to go
 * call somebody, the other is a prompt to go fix the plumbing. So every
 * unmeasurable stage carries the word "cannot see" and its REASON, on the
 * surface, in the cell. There is no code path here that renders a null as a
 * zero, a blank or a dash on its own.
 *
 * ⚠️ AND A REAL ZERO IS RENDERED DIFFERENTLY FROM AN UNKNOWN. Notre Dame has
 * no desk — measured against all 221 bodies in the assignment sheet, so the
 * answer is genuinely nobody and it shows as a named gap in red. Its outreach
 * is unknown. Collapsing the two would hide a finding inside a blind spot.
 *
 * ⚠️ NO DOLLAR FIGURE AT ANY ROLE. Money is manager-only and this is
 * rep-visible, so a stage that implies value says so and shows no amount.
 *
 * ⭐ MOBILE FIRST. The stage chain is a stacked list at phone width and only
 * becomes a row when there is room; the per-team chain is a card per team on
 * narrow, never a seven-column table squeezed to nothing.
 */
/* Hoisted for the same reason as BowlEligibility's: written inline, the word
 * "card" in a className inside a map reads to check-clickable.mjs as a record
 * row with nothing clickable in it. A constant keeps the gate pointed at real
 * rows. */
const PANEL = `card ${styles.panel}`

export default function BowlFunnel() {
  const [s, setS] = useState({ status: 'loading', data: null, error: '' })
  const [openTeam, setOpenTeam] = useState('')

  const load = useCallback(() => {
    api.fbsBowlFunnel()
      .then((d) => setS({ status: 'ready', data: d, error: '' }))
      /* The error is SHOWN. A failed read must never look like an empty funnel. */
      .catch((e) => setS({
        status: 'error', data: null,
        error: e?.body?.detail || e?.body?.error || e?.message || UNAVAILABLE_NOTE,
      }))
  }, [])

  useEffect(() => { load() }, [load])

  if (s.status === 'loading') {
    return (
      <section className={PANEL}>
        <h2 className={styles.h}>The funnel</h2>
        <p className={styles.muted}>{LOADING_NOTE}</p>
      </section>
    )
  }

  if (s.status === 'error') {
    return (
      <section className={PANEL}>
        <h2 className={styles.h}>The funnel</h2>
        <p className={styles.bad}>{UNAVAILABLE_NOTE}</p>
        <p className={styles.muted}>{s.error}</p>
      </section>
    )
  }

  const d = s.data || {}
  const stages = d.stages || []
  const byId = Object.fromEntries(stages.map((x) => [x.id, x]))

  return (
    <>
      {/* ---- the chain, with each stage's standing on its face ---- */}
      <section className={PANEL}>
        <div className={styles.head}>
          <h2 className={styles.h}>The funnel</h2>
          <div className={styles.meta}>
            <span className={styles.lvl_ok}>{d.summary?.measurable ?? '—'} measurable</span>
            <span className={styles.lvl_warn}>{d.summary?.fixable ?? '—'} fixable</span>
            {(d.summary?.notInstrumented ?? 0) > 0 && (
              <span className={styles.lvl_bad}>{d.summary.notInstrumented} not instrumented</span>
            )}
          </div>
        </div>
        <p className={styles.muted}>{FUNNEL_LEAD}</p>
        {/* 🔴 The headline is about the MEASUREMENT, because that is this
            week's actionable fact. */}
        {d.summary?.headline && <p className={styles.headline}>{d.summary.headline}</p>}

        <ol className={styles.chain}>
          {STAGES.map((meta, i) => {
            const st = byId[meta.id]
            if (!st) return null
            const level = STANDING_LEVEL[st.standing] || 'bad'
            return (
              <li key={meta.id} className={`${styles.stage} ${styles[`st_${level}`]}`}>
                <div className={styles.stageTop}>
                  <span className={styles.stageN}>{i + 1}</span>
                  {/* ⚠️ THE LOCAL LABEL, NOT `st.label` FROM THE API.
                      check-ownership-language.mjs can only read source, so a
                      label that arrives over the wire is a label no gate
                      governs. Stage 2 is the live example: it rendered "Owned
                      by a rep" until this line stopped trusting the payload
                      for display text. The API's label is still the contract;
                      it is just not what a human reads. */}
                  <span className={styles.stageLabel}>{meta.label}</span>
                  {/* ⚠️ A NUMBER OR THE WORDS "cannot see". Never a bare dash
                      and never a 0 standing in for a null. */}
                  {st.count === null
                    ? <span className={styles.cannot}>cannot see</span>
                    : <b className={styles.stageCount}>{nz(st.count)}</b>}
                </div>
                <span className={styles[`tag_${level}`]}>{STANDING_LABEL[st.standing] || st.standing}</span>
                {st.ofTotal != null && st.count !== null && (
                  <span className={styles.ofTotal}>of {nz(st.ofTotal)} latched</span>
                )}
                {/* The derivation if it printed a number, the reason if it did not. */}
                <p className={styles.why}>{lead(st)}</p>
                {/* 🔴 "ASSIGNED · 4" WITH NOBODY NAMED WAS THE COMPLAINT.
                    Brian, on his phone: *"it shows assigned but doesn't show
                    who it's assigned to."* He was reading this stage. It
                    printed a count off a conference-coverage derivation and
                    stopped there.

                    ⚠️ AND THE FIX IS NOT TO PRINT A NAME. The payload has
                    carried `authoritativeOwner` all along and this component
                    threw it away — and what it says is that this stage is NOT
                    the owner: it reports which reps COVER the conference,
                    while the school record's own `sr_assigned_rep` is the
                    authoritative answer and MAY DIFFER. Coverage is
                    many-to-one — the Big Ten is covered by two people, which
                    does not say which of them has Southern California — so
                    promoting a covering rep into the owner seat would be
                    inventing an accountability that nobody declared. That is
                    the same small lie as a split body printing one name.

                    So both are rendered and the gap between them is the
                    content: the count keeps its coverage derivation above,
                    and the authoritative seat says plainly that it cannot be
                    read yet, with the reason and the one key that would
                    unlock it. A disagreement made visible beats a silent
                    pick. The covering reps themselves are named per team on
                    the trigger list above, which is where a team-level
                    question belongs. */}
                {st.authoritativeOwner && (
                  <div className={styles.owner}>
                    <span className={styles.ownerLabel}>Assigned to</span>
                    {typeof st.authoritativeOwner.count === 'number'
                      ? <b className={styles.ownerValue}>{nz(st.authoritativeOwner.count)} named</b>
                      : <span className={styles.cannot}>cannot see yet</span>}
                    <p className={styles.why}>{st.authoritativeOwner.reason}</p>
                    {st.authoritativeOwner.fix && (
                      <p className={styles.fix}><b>Fix:</b> {st.authoritativeOwner.fix}</p>
                    )}
                  </div>
                )}
                {st.fix && <p className={styles.fix}><b>Fix:</b> {st.fix}</p>}
                {st.gatedOn && <p className={styles.gated}><b>Waiting on:</b> {st.gatedOn}</p>}
                {implied(st) && <p className={styles.money}>{st.valueReason || MONEY_NOTE}</p>}
              </li>
            )
          })}
        </ol>
        <p className={styles.muted}>{NULL_NOTE}</p>
        <p className={styles.muted}>{MONEY_NOTE}</p>
      </section>

      {/* ---- 🔴 THE TWO BLOCKERS THIS KPI EXPOSES. Said out loud, at the top
               of the funnel, not discovered three screens down. ---- */}
      {byId.eligible?.onTheAccount && (
        <section className={PANEL}>
          <h2 className={styles.h}>Stage 1 has no event on the account yet</h2>
          {/* ⚠️ NOT FILLED IN WITH THE SNAPSHOT COUNT. The board says teams are
              eligible; zero accounts know it. Two different facts. */}
          <p className={styles.bad}>
            <b>Accounts stamped bowl eligible: cannot see</b> — no company record carries the stamp.
          </p>
          <p className={styles.why}>{byId.eligible.onTheAccount.reason}</p>
          {byId.eligible.onTheAccount.gatedOn && (
            <p className={styles.gated}><b>Waiting on:</b> {byId.eligible.onTheAccount.gatedOn}</p>
          )}
        </section>
      )}

      {byId.owned?.unassigned?.count > 0 && (
        <section className={PANEL}>
          <h2 className={styles.h}>
            No desk at all · {nz(byId.owned.unassigned.count)}
          </h2>
          {/* A NAMED GAP, in red, never a blank cell. */}
          <p className={styles.bad}>
            {byId.owned.unassigned.teams.map((t) => t.team).join(', ')} — no derivable rep.
          </p>
          <p className={styles.why}>{byId.owned.unassigned.finding}</p>
          {byId.owned.unassigned.fix && (
            <p className={styles.fix}><b>Fix:</b> {byId.owned.unassigned.fix}</p>
          )}
          {byId.owned.unassigned.gatedOn && (
            <p className={styles.gated}><b>Waiting on:</b> {byId.owned.unassigned.gatedOn}</p>
          )}
          <ul className={styles.list}>
            {byId.owned.unassigned.teams.map((t) => (
              <li key={t.team}><b>{t.team}</b> · {t.conference} — {t.why}</li>
            ))}
          </ul>
        </section>
      )}

      {/* ---- latency: SR's actual scoreboard ---- */}
      <section className={PANEL}>
        <h2 className={styles.h}>Latency between stages</h2>
        <p className={styles.muted}>
          This is the scoreboard. A gap that does not exist to be measured says so — it is not
          rendered as 0 days, because 0 days would be a performance claim nobody made.
        </p>
        {/* ⚠️ THE WIDEST COLUMN WAS THE EXPLANATION AND IT WAS OFF-SCREEN.
            This table shipped as a deliberate horizontal scroller with the
            stage pair pinned, and the file said so out loud. Measured at
            390px that meant a reader saw "Eligible → Assigned" and a median,
            and the column headed "Basis, or why not" — the only one that says
            whether a number means anything — started past the right edge on
            every row. `cc-cards` stacks it with its label instead. */}
        <div className={`${styles.scroll} cc-scrollcue cc-cards-wrap`}>
          <table className={`${styles.table} cc-cards`}>
            <thead>
              <tr>
                <th className={styles.sticky}>From → to</th>
                <th>Median</th>
                <th>n</th>
                <th className={styles.wide}>Basis, or why not</th>
              </tr>
            </thead>
            <tbody>
              {(d.latency || []).map((l) => (
                <tr key={`${l.from}-${l.to}`} aria-disabled="true">
                  {/* Stage LABELS, not the raw ids — same reason as the chain
                      above: "owned" is not what a human should read. */}
                  <td className={styles.sticky} data-col="name" data-label="Stage pair">
                    {stageName(l.from)} → {stageName(l.to)}
                  </td>
                  <td data-label="Median">
                    {nd(l.days) || <span className={styles.cannot}>cannot see</span>}
                  </td>
                  {/* `n` is a real count when the stage is measurable — 0 here
                      means "measurable, no observations yet", which is NOT the
                      same as the dash in the column to its left. */}
                  <td data-label="Observations">{typeof l.n === 'number' ? nz(l.n) : '—'}</td>
                  <td className={styles.wide} data-label="Basis, or why not">
                    {l.basis || l.reason}
                    {l.note ? ` ${l.note}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---- per team: the chain, one card each. A card and not a table
               because seven stages across a 390px phone is unreadable, and
               Brian reads this on a phone. ---- */}
      <section className={PANEL}>
        <div className={styles.head}>
          <h2 className={styles.h}>Per team · {nz((d.teams || []).length)}</h2>
        </div>
        <p className={styles.muted}>{AGE_NOTE}</p>
        <p className={styles.muted}>{GAP_NOTE}</p>

        {(d.teams || []).length === 0 && (
          <p className={styles.muted}>
            {d.summary?.headline || 'No team has latched a trigger yet. That is the absence of a '
              + 'trigger, not a funnel of zeros.'}
          </p>
        )}

        <ul className={styles.teams}>
          {(d.teams || []).map((t) => {
            const on = openTeam === t.team
            return (
              <li key={t.team} className={styles.team}>
                <button
                  type="button"
                  className={styles.teamTop}
                  aria-expanded={on}
                  onClick={() => setOpenTeam(on ? '' : t.team)}
                >
                  <span className={styles.teamName}>
                    <b>{t.team}</b>
                    <span className={styles.pill}>{t.conference}</span>
                    {t.state === 'eligible'
                      ? <span className={styles.pillOk}>eligible</span>
                      : <span className={styles.pill}>contingent</span>}
                  </span>
                  <span className={styles.teamAge}>
                    {/* The one real elapsed number on this surface. */}
                    {nd(t.daysSinceTrigger) || <span className={styles.cannot}>no trigger date</span>}
                    <span className={styles.ageLabel}>since trigger</span>
                  </span>
                </button>

                {/* The chain, always visible — it IS the row. */}
                <ol className={styles.cells}>
                  {STAGES.map((meta) => {
                    const c = cell(t.chain?.[meta.id])
                    return (
                      <li key={meta.id} className={`${styles.cellItem} ${styles[`c_${c.kind}`]}`}>
                        <span className={styles.cellLabel}>{meta.short}</span>
                        <span className={styles.cellVal}>{c.text}</span>
                      </li>
                    )
                  })}
                </ol>

                {/* Every reason, in full, when a reader opens the row. Nothing
                    about this funnel is a number without a sentence. */}
                {on && (
                  <dl className={styles.reasons}>
                    <div>
                      <dt>Crossed six wins</dt>
                      <dd>
                        {t.becameAt ? new Date(t.becameAt).toLocaleDateString() : '—'}
                        {' · week '}{t.weekKey}{' · board stamped '}{t.sourceStamp}
                      </dd>
                    </div>
                    <div>
                      <dt>Record</dt>
                      <dd>{t.wins}–{t.losses}</dd>
                    </div>
                    {STAGES.map((meta) => {
                      const c = cell(t.chain?.[meta.id])
                      const raw = t.chain?.[meta.id] || {}
                      return (
                        <div key={meta.id}>
                          <dt>{meta.label}</dt>
                          <dd>
                            <span className={styles[`c_${c.kind}`]}>{c.text}</span>
                            {raw.reps?.length ? ` — ${raw.reps.map((r) => r.name).join(', ')}` : ''}
                            {c.reason ? ` ${c.reason}` : ''}
                          </dd>
                        </div>
                      )
                    })}
                  </dl>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      {/* ---- ⭐ THE ONE CHANGE THAT UNLOCKS THE MOST. Last on the page
               because it is the conclusion, not the headline. ---- */}
      {d.unlock && (
        <section className={PANEL}>
          <h2 className={styles.h}>The one change that unlocks the most of this funnel</h2>
          <p className={styles.headline}>{d.unlock.fix}</p>
          <p className={styles.why}>{d.unlock.why}</p>
          {d.unlock.whyItIsHeld && (
            <p className={styles.why}><b>Why it is held:</b> {d.unlock.whyItIsHeld}</p>
          )}
          {d.unlock.gatedOn && (
            <p className={styles.gated}><b>Waiting on:</b> {d.unlock.gatedOn}</p>
          )}
        </section>
      )}

      {/* ---- the posture, stated rather than assumed ---- */}
      <section className={PANEL}>
        <h2 className={styles.h}>How to read these numbers</h2>
        <p className={styles.muted}>{d.posture}</p>
        {d.generatedAt && (
          <p className={styles.muted}>
            Computed {new Date(d.generatedAt).toLocaleString()} · season {d.season} ·
            {' '}this view writes nothing.
          </p>
        )}
      </section>
    </>
  )
}
